import React, { useEffect, useRef } from "react";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { isElectronicBookmarkGroupNameTooLong } from "../api/editGropus";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";

type BookmarkManagementCardProps = {
  title: string;
  isActive?: boolean;
  accentColor?: string;
  isEditing?: boolean;
  renameDraft: string;
  isRenaming?: boolean;
  isDeleting?: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onRenameDraftChange: (value: string) => void;
  onSaveRename: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
};

const withHexAlpha = (color: string, alphaHex: string) =>
  /^#[\da-fA-F]{6}$/.test(color) ? `${color}${alphaHex}` : color;

const CARD_SIZE_CLASS = "h-full min-h-[10.75rem] sm:min-h-[11.5rem]";

const BookmarkManagementCard: React.FC<BookmarkManagementCardProps> = ({
  title,
  isActive = false,
  accentColor = "#f6851f",
  isEditing = false,
  renameDraft,
  isRenaming = false,
  isDeleting = false,
  onSelect,
  onStartEdit,
  onRenameDraftChange,
  onSaveRename,
  onCancelRename,
  onDelete,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const isRenameDraftTooLong =
    isElectronicBookmarkGroupNameTooLong(renameDraft);

  useEffect(() => {
    if (!isEditing || isRenaming) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!cardRef.current) return;

      const target = event.target;
      if (target instanceof Node && !cardRef.current.contains(target)) {
        onCancelRename();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isEditing, isRenaming, onCancelRename]);

  return (
    <div
      ref={cardRef}
      role="button"
      aria-pressed={isActive}
      tabIndex={isEditing ? -1 : 0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (isEditing) return;

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={`group relative block w-full overflow-hidden rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] text-left transition duration-200 ease-out ${CARD_SIZE_CLASS}`}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition duration-200 ease-out group-hover:opacity-100 group-focus-within:opacity-100"
        style={{ backgroundColor: withHexAlpha(accentColor, "12") }}
      />

      <div className="relative z-10 flex h-full flex-col px-4 pb-4 pt-4 transition duration-200 ease-out sm:px-5 sm:pb-5">
        <div className="flex min-h-[2.15rem] items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Nazwa grupy
            </p>
            {isEditing ? (
              <div
                className="flex min-w-0 flex-1 items-start"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="text"
                  value={renameDraft}
                  onChange={(event) => onRenameDraftChange(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    event.stopPropagation();

                    if (event.key === "Escape") {
                      event.preventDefault();
                      onCancelRename();
                    }

                    if (event.key === "Enter") {
                      event.preventDefault();
                    }
                  }}
                  autoFocus
                  aria-label="Nazwa grupy"
                  className={`min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-base font-semibold leading-[1.1rem] tracking-[-0.04em] outline-none transition focus:border-[#f6851f] focus:ring-4 focus:ring-[#f6851f]/10 sm:text-[0.95rem] ${
                    isRenameDraftTooLong ? "text-red-600" : "text-slate-900"
                  }`}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onStartEdit();
                }}
                onKeyDown={(event) => event.stopPropagation()}
                className="min-w-0 rounded-xl text-left transition hover:text-slate-700"
                aria-label={`Edytuj nazwę grupy ${title}`}
              >
                <span className="break-words text-sm font-semibold leading-[1.1rem] tracking-[-0.04em] text-slate-900 line-clamp-2 sm:text-[0.95rem]">
                  {title}
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center py-3">
          <div
            className="flex items-center justify-center gap-3"
            onClick={(event) => event.stopPropagation()}
          >
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSaveRename();
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  disabled={isRenaming}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME} disabled:opacity-50`}
                  style={getActionButtonStyle("orange")}
                  aria-label="Zapisz nazwę grupy"
                >
                  <Check size={17} strokeWidth={2.4} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCancelRename();
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  disabled={isRenaming}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME} disabled:opacity-50`}
                  style={getActionButtonStyle("slate")}
                  aria-label="Anuluj zmianę nazwy"
                >
                  <X size={17} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onStartEdit();
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                  style={getActionButtonStyle("slate")}
                  aria-label={`Edytuj nazwę grupy ${title}`}
                >
                  <Pencil size={18} strokeWidth={2.1} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  disabled={isDeleting}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME} disabled:opacity-50`}
                  style={getActionButtonStyle("red")}
                  aria-label={`Usuń grupę ${title}`}
                >
                  <Trash2 size={18} strokeWidth={2.1} aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookmarkManagementCard;
